import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Funnel, ChartLineUp, Clock, Users, Package,
    TrendUp, FileDoc, ShoppingCart, Receipt, HandCoins,
    Hourglass, WarningOctagon, Target, UsersThree, Rocket, Timer,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

import KpiTile from "./KpiTile";
import FunnelStage from "./FunnelStage";
import CycleTimeTile from "./CycleTimeTile";
import TrendChart from "./TrendChart";
import DropOffPanel from "./DropOffPanel";
import DrillDownDialog from "./DrillDownDialog";
import FilterBar from "./FilterBar";
import LeaderboardTable from "./LeaderboardTable";
import { EMPTY_FILTERS, STAGE_LABEL } from "./constants";

export default function PipelineAnalytics() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [drill, setDrill] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = Object.fromEntries(
                    Object.entries(filters).filter(([, v]) => v !== "" && v != null),
                );
                const { data } = await api.get("/billing/reports/pipeline", { params });
                if (!cancelled) setData(data);
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err, "Failed to load pipeline"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [filters]);

    const stagesMaxValue = useMemo(
        () => Math.max(1, ...(data?.funnel?.stages?.map((s) => s.count) || [1])),
        [data],
    );

    const reset = () => setFilters(EMPTY_FILTERS);

    if (loading && !data) {
        return (
            <div data-testid="pipeline-page-loading">
                <PageHeader eyebrow="Pipeline analytics" title="Loading…" />
            </div>
        );
    }
    if (!data) return null;

    const h = data.headline;
    const ct = data.cycle_times;

    // Drop-off + top-delayed column definitions (kept here because they reference
    // page-level link routes and don't repeat across components).
    const colsLeadsNoProposal = [
        { key: "name", label: "Lead", render: (r) => <Link to={`/leads/${r.id}`} className="font-semibold text-slate-900 hover:underline">{r.name}</Link> },
        { key: "company", label: "Company" },
        { key: "status", label: "Stage", render: (r) => <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold border bg-violet-50 text-violet-700 border-violet-200">{r.status}</span> },
        { key: "source", label: "Source", render: (r) => r.source || "—" },
        { key: "age_days", label: "Age", align: "right", render: (r) => `${r.age_days || 0}d` },
    ];
    const colsProposalsNoPo = [
        { key: "proposal_number", label: "Proposal", render: (r) => <Link to={`/proposals/${r.id}`} className="font-mono font-semibold text-slate-900 hover:underline">{r.proposal_number}</Link> },
        { key: "client_name", label: "Client", render: (r) => <Link to={`/clients/${r.client_id}/ledger`} className="hover:underline">{r.client_name}</Link> },
        { key: "module_code", label: "Module" },
        { key: "amount", label: "Value", align: "right", render: (r) => formatINR(r.amount) },
        { key: "age_days", label: "Age", align: "right", render: (r) => `${r.age_days?.toFixed?.(0) || 0}d` },
        { key: "created_by_name", label: "Sales rep", render: (r) => r.created_by_name || "—" },
    ];
    const colsPosNoInvoice = [
        { key: "po_number", label: "PO", render: (r) => <Link to={`/purchase-orders/${r.id}`} className="font-mono font-semibold text-slate-900 hover:underline">{r.po_number}</Link> },
        { key: "client_name", label: "Client", render: (r) => <Link to={`/clients/${r.client_id}/ledger`} className="hover:underline">{r.client_name}</Link> },
        { key: "po_total", label: "PO Total", align: "right", render: (r) => formatINR(r.po_total) },
        { key: "age_days", label: "Days idle", align: "right", render: (r) => `${r.age_days?.toFixed?.(0) || 0}d` },
        { key: "from_proposal_number", label: "From proposal" },
    ];
    const colsInvoicesUnpaid = [
        { key: "invoice_number", label: "Invoice", render: (r) => <Link to={`/invoices/${r.id}`} className="font-mono font-semibold text-slate-900 hover:underline">{r.invoice_number}</Link> },
        { key: "client_name", label: "Client", render: (r) => <Link to={`/clients/${r.client_id}/ledger`} className="hover:underline">{r.client_name}</Link> },
        { key: "outstanding", label: "Outstanding", align: "right", render: (r) => <span className="text-rose-700 font-semibold">{formatINR(r.outstanding)}</span> },
        { key: "days_overdue", label: "Overdue", align: "right", render: (r) => r.days_overdue > 0 ? <span className="text-rose-700 font-semibold">{r.days_overdue}d</span> : "—" },
        { key: "due_date", label: "Due", render: (r) => formatDate(r.due_date) },
    ];

    const colsTopDelayed = [
        { key: "proposal_number", label: "Proposal", render: (r) => <Link to={`/proposals/${r.proposal_id}`} className="font-mono font-semibold text-slate-900 hover:underline">{r.proposal_number}</Link> },
        { key: "client_name", label: "Client", render: (r) => <Link to={`/clients/${r.client_id}/ledger`} className="hover:underline">{r.client_name}</Link> },
        { key: "module_code", label: "Module" },
        { key: "current_stage", label: "Stage", render: (r) => <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold border bg-slate-50 text-slate-700 border-slate-200">{STAGE_LABEL[r.current_stage] || r.current_stage}</span> },
        { key: "amount", label: "Deal value", align: "right", render: (r) => formatINR(r.amount) },
        { key: "outstanding", label: "Outstanding", align: "right", render: (r) => r.outstanding > 0 ? <span className="text-rose-700 font-semibold">{formatINR(r.outstanding)}</span> : "—" },
        { key: "days_in_pipeline", label: "Days", align: "right", render: (r) => `${r.days_in_pipeline.toFixed(0)}d` },
        { key: "created_by_name", label: "Sales rep" },
    ];

    return (
        <div data-testid="pipeline-page">
            <PageHeader
                eyebrow="Reports"
                title={
                    <span className="flex items-center gap-3">
                        <Funnel size={22} weight="duotone" className="text-indigo-500" />
                        Pipeline analytics
                    </span>
                }
                description="Where deals get stuck · who converts better · where revenue leaks."
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                <FilterBar filters={filters} options={data.filter_options} onChange={setFilters} onReset={reset} />

                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="pipeline-kpis">
                    <KpiTile label="Leads" value={h.leads ?? 0} hint={`${h.leads_converted ?? 0} converted · ${h.lead_to_proposal_pct ?? 0}%`} icon={<UsersThree size={11} />} tone="violet" testid="kpi-leads" hrefRoute="/leads" />
                    <KpiTile label="Proposals" value={h.proposals} hint={formatINR(h.proposals_value) + " value"} icon={<FileDoc size={11} />} tone="indigo" testid="kpi-proposals" hrefRoute="/proposals" />
                    <KpiTile label="POs Finalized" value={h.pos_finalized} hint={formatINR(h.pos_value) + " value"} icon={<ShoppingCart size={11} />} tone="amber" testid="kpi-pos" hrefRoute="/purchase-orders" />
                    <KpiTile label="Invoiced" value={h.invoiced_pos} hint={formatINR(h.invoices_value)} icon={<Receipt size={11} />} testid="kpi-invoiced" hrefRoute="/invoices" />
                    <KpiTile label="Paid" value={h.paid_invoices} hint={formatINR(h.paid_value)} icon={<HandCoins size={11} />} tone="emerald" testid="kpi-paid" />
                    <KpiTile label="Overall conversion" value={`${h.overall_conversion_pct}%`} hint="Proposal → Paid" icon={<Target size={11} />} tone={h.overall_conversion_pct >= 50 ? "emerald" : h.overall_conversion_pct >= 25 ? "amber" : "rose"} testid="kpi-conversion" />
                </div>

                <section className="space-y-3">
                    <SectionHeading icon={<Funnel size={11} weight="fill" />} eyebrow="Conversion funnel" title="Stage-wise drop-off & conversion"
                        description={data.funnel.lead_stage_active ? "Lead stage is active — the funnel spans Lead → Proposal → PO → Invoice → Paid." : null} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="pipeline-funnel">
                        {data.funnel.stages.map((stage, i) => (
                            <FunnelStage key={stage.key} stage={stage} isFirst={i === 0} valueWidthPct={(stage.count / stagesMaxValue) * 100} />
                        ))}
                    </div>
                </section>

                <section className="space-y-3">
                    <SectionHeading icon={<Clock size={11} />} eyebrow="Cycle time distribution" title="Median + P25/P75 across the deal lifecycle" />
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pipeline-cycle-times">
                        <CycleTimeTile label="Proposal → PO" data={ct.proposal_to_po} />
                        <CycleTimeTile label="PO → Invoice" data={ct.po_to_invoice} />
                        <CycleTimeTile label="Invoice → Payment" data={ct.invoice_to_payment} />
                        <CycleTimeTile label="Proposal → Payment" data={ct.proposal_to_payment} />
                    </div>
                </section>

                <section className="space-y-3">
                    <SectionHeading icon={<WarningOctagon size={11} weight="fill" />} eyebrow="Drop-off analysis" title="Where deals get stuck" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3">
                        <DropOffPanel
                            title="Leads without proposal"
                            items={data.drop_off.leads_no_proposal || []}
                            columns={colsLeadsNoProposal}
                            accent={<UsersThree size={18} className="text-violet-600" weight="duotone" />}
                            testid="dropoff-leads-no-proposal"
                            openDrillDown={() => setDrill({ title: "Leads without proposal", items: data.drop_off.leads_no_proposal || [], columns: colsLeadsNoProposal })}
                        />
                        <DropOffPanel
                            title="Proposals not converted to PO"
                            items={data.drop_off.proposals_no_po}
                            columns={colsProposalsNoPo}
                            accent={<FileDoc size={18} className="text-indigo-600" weight="duotone" />}
                            testid="dropoff-proposals-no-po"
                            openDrillDown={() => setDrill({ title: "Proposals not converted to PO", items: data.drop_off.proposals_no_po, columns: colsProposalsNoPo })}
                        />
                        <DropOffPanel
                            title="POs not yet invoiced"
                            items={data.drop_off.pos_no_invoice}
                            columns={colsPosNoInvoice}
                            accent={<ShoppingCart size={18} className="text-amber-600" weight="duotone" />}
                            testid="dropoff-pos-no-invoice"
                            openDrillDown={() => setDrill({ title: "POs not yet invoiced", items: data.drop_off.pos_no_invoice, columns: colsPosNoInvoice })}
                        />
                        <DropOffPanel
                            title="Invoices not paid"
                            items={data.drop_off.invoices_unpaid}
                            columns={colsInvoicesUnpaid}
                            accent={<Receipt size={18} className="text-rose-600" weight="duotone" />}
                            testid="dropoff-invoices-unpaid"
                            openDrillDown={() => setDrill({ title: "Invoices not paid", items: data.drop_off.invoices_unpaid, columns: colsInvoicesUnpaid })}
                        />
                    </div>
                </section>

                {(data.by_lead_source || []).length > 0 && (
                    <section className="space-y-3">
                        <SectionHeading icon={<Rocket size={11} weight="fill" />} eyebrow="Lead source ROI" title="Conversion value + velocity by acquisition channel"
                            description="Revenue delivered per source, plus average days from first contact to proposal." />
                        <div className="reedos-card overflow-hidden" data-testid="pipeline-by-lead-source">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr className="text-left">
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px]">Source</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Leads</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Converted</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Conv %</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Proposal ₹</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">PO ₹</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Invoiced ₹</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Paid ₹</th>
                                            <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Avg days</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.by_lead_source.map((s) => (
                                            <tr key={s.source} className="border-t border-slate-100 reedos-row-hover" data-testid={`pipeline-lead-source-${s.source}`}>
                                                <td className="px-4 py-2.5 text-sm font-semibold text-slate-900">{s.source}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data">{s.leads_count}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-indigo-700">{s.converted_count}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold">
                                                    <span className={s.conversion_pct >= 50 ? "text-emerald-700" : s.conversion_pct >= 25 ? "text-amber-700" : "text-rose-700"}>
                                                        {s.conversion_pct}%
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600">{formatINR(s.proposal_value)}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-amber-700">{formatINR(s.po_value)}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-700">{formatINR(s.invoice_value)}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-emerald-700">{formatINR(s.paid_value)}</td>
                                                <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-500">
                                                    {s.avg_days_to_conversion !== null ? (
                                                        <span className="inline-flex items-center gap-1"><Timer size={10} /> {s.avg_days_to_conversion}d</span>
                                                    ) : "—"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                )}

                <section className="space-y-3">
                    <SectionHeading icon={<Hourglass size={11} weight="fill" />} eyebrow="Top delayed deals" title="Longest in pipeline + highest outstanding" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <TopDelayedTable title="Longest cycle time" subtitle="Open deals with most days in pipeline" icon={<ChartLineUp size={11} />} rows={data.top_delayed.by_cycle_time} cols={colsTopDelayed} testid="top-delayed-cycle" emptyMsg="No open deals with cycle data." />
                        <TopDelayedTable title="Highest outstanding" subtitle="Most receivable across the deal" icon={<HandCoins size={11} />} rows={data.top_delayed.by_outstanding} cols={colsTopDelayed} testid="top-delayed-outstanding" emptyMsg="No outstanding receivables." />
                    </div>
                </section>

                <section className="space-y-3">
                    <SectionHeading icon={<TrendUp size={11} weight="fill" />} eyebrow="Monthly trends" title="12-month rolling activity" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <TrendChart months={data.trends.months} series={data.trends.proposals} accent={{ key: "proposals", icon: <FileDoc size={11} />, label: "Proposals raised", barClass: "bg-gradient-to-t from-indigo-500 to-indigo-400" }} />
                        <TrendChart months={data.trends.months} series={data.trends.pos_finalized} accent={{ key: "pos", icon: <ShoppingCart size={11} />, label: "POs finalized", barClass: "bg-gradient-to-t from-amber-500 to-amber-400" }} />
                        <TrendChart months={data.trends.months} series={data.trends.invoices} accent={{ key: "invoices", icon: <Receipt size={11} />, label: "Invoices raised", barClass: "bg-gradient-to-t from-slate-700 to-slate-500" }} />
                        <TrendChart months={data.trends.months} series={data.trends.payments} accent={{ key: "payments", icon: <HandCoins size={11} />, label: "Payments received", barClass: "bg-gradient-to-t from-emerald-500 to-emerald-400" }} />
                    </div>
                </section>

                <section className="space-y-3">
                    <SectionHeading icon={<Users size={11} weight="fill" />} eyebrow="Performance leaderboards" title="By sales rep · By module" />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <LeaderboardTable title="By sales rep" icon={<Users size={11} />} rows={data.leaderboards.by_sales_rep}
                            keyHeader="Sales rep" testid="leaderboard-rep"
                            renderKey={(r) => <div><div className="font-medium text-slate-900">{r.display_name}</div><div className="text-[10px] text-slate-500">{r.email}</div></div>} />
                        <LeaderboardTable title="By module" icon={<Package size={11} />} rows={data.leaderboards.by_module}
                            keyHeader="Module" testid="leaderboard-module"
                            renderKey={(r) => <span className="font-mono font-semibold text-slate-900">{r.key}</span>} />
                    </div>
                </section>
            </div>

            <DrillDownDialog
                open={!!drill}
                onOpenChange={(v) => { if (!v) setDrill(null); }}
                title={drill?.title || ""}
                items={drill?.items || []}
                columns={drill?.columns || []}
            />
        </div>
    );
}

function SectionHeading({ icon, eyebrow, title, description }) {
    return (
        <div>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {eyebrow}</div>
            <h3 className="font-display text-lg font-semibold text-slate-900 mt-0.5">{title}</h3>
            {description && <p className="text-[11px] text-slate-400 mt-0.5 italic">{description}</p>}
        </div>
    );
}

function TopDelayedTable({ title, subtitle, icon, rows, cols, testid, emptyMsg }) {
    return (
        <div className="reedos-card overflow-hidden" data-testid={testid}>
            <div className="px-5 py-3 border-b border-slate-200">
                <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
            </div>
            {rows.length === 0 ? (
                <div className="px-5 py-6 text-center text-xs text-slate-500">{emptyMsg}</div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left">
                            {cols.map((c) => (
                                <th key={c.key} className={classJoin("px-3 py-2 reedos-eyebrow text-[9px]", c.align === "right" && "text-right")}>{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.proposal_id} className="border-t border-slate-100 reedos-row-hover">
                                {cols.map((c) => (
                                    <td key={c.key} className={classJoin("px-3 py-2 text-xs", c.align === "right" && "text-right reedos-data")}>
                                        {c.render ? c.render(row) : row[c.key] || "—"}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
